import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { buildUrl, fetchJson } from "../shared/api";
import { getMajor, isVersionHigher, isReleaseCandidate } from "../shared/version";
import type { IdmInfo, Version } from "../shared/types/api";
import { useTranslation } from "react-i18next";

type IdMsMap = Record<string, ReturnType<typeof createEmptyRepoIdm>>;

function createEmptyRepoIdm(idm: IdmInfo) {
    return {
        ...idm,
        uniqueKey: `${idm.nameOfRepo}-${idm.tableName}`,
        version: "",
        minVersionOfProduct: "",
        versions: [] as Version[],
    };
}

function shouldKeepVersion(ver: Version, repoName: string, filterOld: boolean): boolean {
    if (!filterOld) return true;

    if (repoName === "CzechIdMng") {
        return getMajor(ver.tag) >= 12;
    }
    return getMajor(ver.parentVersion) >= 12;
}

function IdMsPage() {
    const { t } = useTranslation();
    const [idmData, setIdmData] = useState<IdMsMap>({});
    const [lts, setLts] = useState<ReturnType<typeof createEmptyRepoIdm> | undefined>();
    const [latest, setLatest] = useState<ReturnType<typeof createEmptyRepoIdm> | undefined>();
    const [filterOldVersions] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const [idms, versions] = await Promise.all([
                    fetchJson<IdmInfo[]>(buildUrl("/idm/all")),
                    fetchJson<Version[]>(buildUrl("/version/all")),
                ]);

                if (cancelled) return;

                const idmsMap: IdMsMap = {};

                let latestIdm: ReturnType<typeof createEmptyRepoIdm> | undefined;
                let latestLts: ReturnType<typeof createEmptyRepoIdm> | undefined;

                for (const mod of idms) {
                    if (!mod.nameOfRepo) continue;

                    if (!latestIdm) {
                        latestIdm = createEmptyRepoIdm(mod);
                        //continue;
                    }

                    if (!latestLts && mod.lts) {
                        latestLts = createEmptyRepoIdm(mod);
                        //continue;
                    }

                    const key = `${mod.nameOfRepo}-${mod.tableName}`;
                    idmsMap[key] = createEmptyRepoIdm(mod);
                }

                const versionsByRepo: Record<string, Version[]> = {};
                for (const ver of versions) {
                    if (!ver.repository || !ver.tag || ver.tag.toLowerCase().includes("snapshot") || isReleaseCandidate(ver.tag)) continue;
                    (versionsByRepo[ver.repository] ??= []).push(ver);
                }

                const applyVersions = (repo: any, isIdm: boolean) => {
                    const repoVersions = versionsByRepo[repo.nameOfRepo] || [];

                    for (const ver of repoVersions) {
                        if (!shouldKeepVersion(ver, repo.nameOfRepo, !isIdm && filterOldVersions)) {
                            continue;
                        }

                        let shouldUpdate = true;

                        if (isIdm) {
                            const match = repo.tableName.match(/IdM\s+(\d+)\.x\.x/);
                            const tableMajor = match ? Number(match[1]) : null;
                            if (tableMajor !== null) {
                                shouldUpdate = getMajor(ver.tag) === tableMajor;
                            }
                        }

                        if (shouldUpdate && (!repo.version || isVersionHigher(ver.tag, repo.version))) {
                            repo.versions.push(ver);
                            repo.version = ver.tag;
                            repo.minVersionOfProduct = ver.parentVersion;
                        }
                    }
                };

                if (latestIdm) applyVersions(latestIdm, true);
                if (latestLts) applyVersions(latestLts, true);

                for (const key of Object.keys(idmsMap)) applyVersions(idmsMap[key], true);

                setLatest(latestIdm);
                setLts(latestLts);
                setIdmData(idmsMap);
            } catch (error) {
                if (!cancelled) console.error("Error fetching module/version data:", error);
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [filterOldVersions]);

    return (
        <>
            <div className="summary-grid">
                {latest && (
                    <div className="summary-card">
                        <div className="summary-card__label">{t("idmPage.cards.newest")}</div>
                        <div className="summary-card__title">{latest.tableName}</div>
                        <div className="summary-card__link">
                            {t("idmPage.cards.version")}: <strong>{<Link to={`/release-note?module-version=${encodeURIComponent(latest.nameOfRepo + "_" + latest.version)}`}>{latest.version}</Link> || "—"}</strong>
                        </div>
                        {latest.version && (
                            <Link className="link-detail"
                                to={`/idmdetail?repository=${encodeURIComponent(latest.nameOfRepo)}&major=${encodeURIComponent(
                                    latest.version.split(".")[0]
                                )}`}
                            >
                                {t("idmPage.cards.showDetail")}
                            </Link>
                        )}
                    </div>
                )}

                {lts && (
                    <div className="summary-card">
                        <div className="summary-card__label">{t("idmPage.cards.lts")}</div>
                        <div className="summary-card__title">{lts.tableName}</div>
                        <div className="summary-card__link">
                            {t("idmPage.cards.version")}: <strong>{<Link to={`/release-note?module-version=${encodeURIComponent(lts.nameOfRepo + "_" + lts.version)}`}>{lts.version}</Link> || "—"}</strong>
                        </div>
                        {lts.version && (
                            <Link className="link-detail"
                                to={`/idmdetail?repository=${encodeURIComponent(lts.nameOfRepo)}&major=${encodeURIComponent(
                                    lts.version.split(".")[0]
                                )}`}
                            >
                                {t("idmPage.cards.showDetail")}
                            </Link>
                        )}
                    </div>
                )}
            </div>

            <h1 className="section-title">
                {t("idmPage.olderVersions")}
            </h1>

            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th>{t("idmPage.table.idm")}</th>
                            <th>{t("idmPage.table.latestVersion")}</th>
                            <th>{t("idmPage.table.releaseNotes")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.values(idmData).map((repo: any) => {
                            const moduleVersionParam = encodeURIComponent(repo.nameOfRepo + "_" + repo.version);
                            const releaseNotesUrl = `/release-note?module-version=${moduleVersionParam}`;

                            const major = repo.version ? repo.version.split(".")[0] : "";
                            const idmDetail = `/idmdetail?repository=${encodeURIComponent(repo.nameOfRepo)}&major=${encodeURIComponent(
                                major
                            )}`;

                            return (
                                <tr key={repo.uniqueKey}>
                                    <td>
                                        <button className="icon-button"
                                            title={t("idm.actions.viewVersions")}
                                        >
                                            <Link to={idmDetail}><FontAwesomeIcon icon={faMagnifyingGlass} /></Link>
                                        </button>
                                    </td>
                                    <td>{repo.tableName}</td>
                                    <td>{repo.version ? <Link to={releaseNotesUrl}>{repo.version}</Link> : ""}</td>
                                    <td>
                                        <Link to={idmDetail}>{t("idm.actions.notes")}</Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default IdMsPage;